'use client';

import { useCallback } from 'react';
import { useProfile } from '../queries/useProfileQueries';
import { useBandFreeCases, useBandStatus } from '../queries/useBandQueries';
import { CaseDetailsResponseOpenCurrencyEnum } from '../services/api';
import type { CaseDetailsResponse } from '../services/api';

interface UseCaseAffordabilityArgs {
  caseId?: string;
  openCurrency?: CaseDetailsResponse['openCurrency'];
  priceInCredits?: number;
  qty?: number;
}

export interface CaseAffordability {
  isBonusCase: boolean;
  isLocked: boolean;
  hasFreeOpen: boolean;
  availableBalance: number | undefined;
  cost: number;
  hasInsufficientBalance: boolean;
  isBlocked: boolean;
  missingAmount: number;
  canAfford: (qty: number) => boolean;
}

export function useCaseAffordability({
  caseId,
  openCurrency,
  priceInCredits,
  qty = 1,
}: UseCaseAffordabilityArgs): CaseAffordability {
  const { data: profile } = useProfile();
  const { data: bandStatus } = useBandStatus();
  const { data: freeCases } = useBandFreeCases();

  const isBonusCase = openCurrency === CaseDetailsResponseOpenCurrencyEnum.Bonus;
  const isBandMember = bandStatus?.isMember ?? false;
  const isLocked = isBonusCase && !isBandMember;
  const availableBalance = isBonusCase ? bandStatus?.bonusBalance : profile?.balanceInCredits;

  const freeState = isBonusCase ? freeCases?.cases.find((s) => s.caseId === caseId) : undefined;
  const hasFreeOpen = !!freeState && freeState.unlocked && !freeState.claimedToday;

  const freeOpenResolved = !isBonusCase || freeCases !== undefined;

  const gateReady =
    !isLocked && freeOpenResolved && availableBalance !== undefined && priceInCredits !== undefined;

  const costOf = useCallback(
    (n: number) => (priceInCredits ?? 0) * Math.max(0, n - (hasFreeOpen ? 1 : 0)),
    [priceInCredits, hasFreeOpen],
  );

  const canAfford = useCallback(
    (n: number) => (gateReady ? costOf(n) <= availableBalance : true),
    [gateReady, availableBalance, costOf],
  );

  const cost = costOf(qty);
  const hasInsufficientBalance = gateReady ? cost > availableBalance : false;
  const missingAmount = hasInsufficientBalance && availableBalance !== undefined ? cost - availableBalance : 0;

  return {
    isBonusCase,
    isLocked,
    hasFreeOpen,
    availableBalance,
    cost,
    hasInsufficientBalance,
    isBlocked: isLocked || hasInsufficientBalance,
    missingAmount,
    canAfford,
  };
}
